import React from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import moment from 'moment'
import MonthOfYear from 'common/logic/MonthOfYear'
import { BudgetPlan } from 'types'

interface Props {
    onSuccess: (result: unknown) => void
}

function CreatePlanWorkflow({ onSuccess }: Props) {

    const loadingStep = () => {
        return API.get('/budget-plan').then(r => ({ plans: (r.data as any).results as BudgetPlan[] }))
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, previousValues: unknown, _validationErrors: unknown, completion_callback: (input: unknown) => void) => {
        return { title: "New budget plan", component: form(loading_result as { plans: BudgetPlan[] }, previousValues, completion_callback) }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        const input = user_input as { month: string; copyFrom?: string }
        if (input.copyFrom) {
            return API.post(`/budget-plan/${input.month}?copyFrom=${input.copyFrom}`)
        } else {
            return API.post(`/budget-plan/${input.month}`)
        }
    }

    function form(loading_result: { plans: BudgetPlan[] }, previousValues: unknown, completion: (input: unknown) => void) {
        const parseForm = (values: { month: number; year: number; copyFrom: string }) => {
            completion({
                month: new MonthOfYear(values.month, values.year).toString(),
                copyFrom: values.copyFrom
            })
        }

        const schema = yup.object().shape({
            month: yup.number().required().integer().min(1).max(12),
            year: yup.number().required().integer().min(1000).max(9999),
            copyFrom: yup.number()
        })

        const sortedPlans = [...loading_result.plans].sort((a, b) => b.validFrom.localeCompare(a.validFrom))
        const plans = sortedPlans.map((item) => <option value={item.id} key={item.id}>{item.validFrom}</option>)

        const prev = previousValues as { copyFrom?: string; month?: number; year?: number } | null
        const initialValues = prev ? prev : {
            copyFrom: sortedPlans.length > 0 ? String(sortedPlans[0].id) : "",
            month: moment().month() + 1,
            year: moment().year()
        }

        return <>
            <Formik
                validationSchema={schema}
                onSubmit={parseForm as any}
                initialValues={initialValues as any}
            >
                {({
                    handleSubmit,
                    handleChange,
                    values,
                    errors,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Row>
                            <Form.Group as={Col} controlId="formYear">
                                <Form.Label>Valid from year</Form.Label>
                                <Form.Control
                                    type='text'
                                    name='year'
                                    placeholder="2020"
                                    value={(values as any).year}
                                    onChange={handleChange}
                                    isInvalid={!!(errors as any).year}
                                />
                                <Form.Control.Feedback type="invalid">{(errors as any).year}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} controlId="formMonth">
                                <Form.Label>Month</Form.Label>
                                <Form.Control
                                    type='text'
                                    name='month'
                                    placeholder="03"
                                    value={(values as any).month}
                                    onChange={handleChange}
                                    isInvalid={!!(errors as any).month}
                                />
                                <Form.Control.Feedback type="invalid">{(errors as any).month}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>

                        <Form.Group controlId="formCopy">
                            <Form.Label>Copy expected movements from</Form.Label>
                            <Form.Control
                                as='select'
                                name='copyFrom'
                                value={(values as any).copyFrom}
                                onChange={handleChange}
                                isInvalid={!!(errors as any).copyFrom}
                            >
                                <option value=""></option>
                                {plans}
                            </Form.Control>
                        </Form.Group>
                        <Button variant="primary" type="submit">Save</Button>

                    </Form>
                )}
            </Formik>
        </>
    }

    return <OperationWorkflowModal
        loadingStep={loadingStep}
        userInputStep={userInputStep}
        executionStep={executionStep}
        completionCallback={(result) => onSuccess(result)}
    />
}

export default CreatePlanWorkflow
